/**
 * Compliance → Regulatory Coverage (G4 - doc 22 §7).
 *
 * Single page that surfaces the platform's regulatory-coverage story
 * for a DPO / CISO / Compliance Analyst doing their first or quarterly
 * review:
 *
 *   1. DDR Rule Catalogue - every built-in rule grouped by regulation
 *      citation (SEBI / RBI / BFSI / Healthcare / DPDPA / Cloud / Other)
 *      with a severity breakdown per group. This is what the engine
 *      enforces day-to-day.
 *   2. Evidence Pack Bundles - four framework cards (DPDPA, SEBI_CSCRF,
 *      GDPR, CCPA). Each shows last-built timestamp, file count, total
 *      bytes, manifest signature truncated, and a "Build now" CTA.
 *   3. SDF Designation - DPDPA §10 status from /v1/compliance/sdf-obligations.
 *      Designated-as-SDF / DPO appointed / annual DPIA cadence visible
 *      in one block; nudges the DPO if any field is missing.
 *   4. Cross-Border Inventory - rollup of recent egress destinations
 *      from the audit chain. Shows where data has gone in the period
 *      so the DPO can sanity-check SCC coverage.
 *
 * No new backend endpoints: every data source already exists. The
 * value is in the synthesis.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Download,
  Globe,
  FileText,
  Award,
  Layers,
  Hash,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  listDetectionRules,
  listEvidencePacks,
  buildEvidencePack,
  downloadEvidencePack,
  getSdf,
  listEgressLog,
  getDiscoveryBoardSummary,
  type DetectionRule,
  type EvidencePack,
  type EvidencePackFramework,
  type SdfObligation,
  type EgressLogEntry,
  type DiscoveryBoardSummary,
} from '@/lib/api';

// ── regulation categorisation ─────────────────────────────────────

/**
 * Map a rule name prefix to a regulation-citation bucket. Mirrors the
 * grouping in `apps/api-controller/src/modules/detection/built-in-rules.ts`.
 * Add new prefixes here when the rule catalogue grows.
 */
function categoriseRule(rule: DetectionRule): {
  bucket: 'sebi_rbi_bfsi' | 'healthcare' | 'dpdpa' | 'cloud' | 'foundational';
  citation: string;
} {
  const name = rule.name.toLowerCase();
  if (name.startsWith('bfsi_')) {
    if (name.includes('trading_order') || name.includes('demat') || name.includes('audit_log_truncation') || name.includes('chinese')) {
      return { bucket: 'sebi_rbi_bfsi', citation: 'SEBI CSCRF / Market regs' };
    }
    if (name.includes('upi') || name.includes('cibil')) return { bucket: 'sebi_rbi_bfsi', citation: 'RBI Payment Systems / CIC Act' };
    if (name.includes('pan_cross_border') || name.includes('kyc') || name.includes('cross_bu_grant')) return { bucket: 'sebi_rbi_bfsi', citation: 'PMLA / DPDPA §16' };
    return { bucket: 'sebi_rbi_bfsi', citation: 'SEBI CSCRF' };
  }
  if (name.startsWith('health_')) return { bucket: 'healthcare', citation: 'HIPAA / DPDPA §9' };
  if (name.startsWith('dpdpa_')) return { bucket: 'dpdpa', citation: 'DPDPA Rules 2025' };
  if (name.startsWith('cloud_')) return { bucket: 'cloud', citation: 'Cloud posture (CIS / AWS / Azure / GCP)' };
  return { bucket: 'foundational', citation: 'UIDAI / DPDPA / cross-regulation foundational' };
}

const BUCKET_META: Record<
  ReturnType<typeof categoriseRule>['bucket'],
  { label: string; accent: string; icon: typeof ShieldCheck }
> = {
  sebi_rbi_bfsi: { label: 'BFSI · SEBI / RBI / PMLA', accent: 'from-amber-400 to-amber-600', icon: Award },
  healthcare: { label: 'Healthcare · HIPAA / DPDPA §9', accent: 'from-rose-400 to-rose-600', icon: ShieldCheck },
  dpdpa: { label: 'DPDPA (India) · 2023 + Rules 2025', accent: 'from-emerald-400 to-emerald-600', icon: FileText },
  cloud: { label: 'Cloud Posture · CIS / AWS / Azure / GCP', accent: 'from-sky-400 to-sky-600', icon: Layers },
  foundational: { label: 'Foundational · UIDAI / cross-reg', accent: 'from-slate-400 to-slate-600', icon: Hash },
};

const SEVERITY_VARIANT: Record<string, 'critical' | 'warning' | 'glass'> = {
  critical: 'critical',
  high: 'warning',
  medium: 'glass',
  low: 'glass',
};

const FRAMEWORK_META: Record<
  EvidencePackFramework,
  { displayName: string; citation: string; fileCount: number }
> = {
  DPDPA: { displayName: 'DPDPA + Rules 2025', citation: 'Digital Personal Data Protection Act (India)', fileCount: 10 },
  SEBI_CSCRF: { displayName: 'SEBI CSCRF', citation: 'Cybersecurity & Cyber Resilience Framework', fileCount: 8 },
  GDPR: { displayName: 'GDPR', citation: 'EU General Data Protection Regulation', fileCount: 5 },
  CCPA: { displayName: 'CCPA / CPRA', citation: 'California Consumer Privacy Act', fileCount: 5 },
};

const FRAMEWORK_ORDER: EvidencePackFramework[] = ['DPDPA', 'SEBI_CSCRF', 'GDPR', 'CCPA'];

// ── page ──────────────────────────────────────────────────────────

export function RegulatoryCoverage(): JSX.Element {
  const [rules, setRules] = useState<DetectionRule[] | null>(null);
  const [packs, setPacks] = useState<EvidencePack[] | null>(null);
  const [sdf, setSdf] = useState<SdfObligation | null>(null);
  const [egress, setEgress] = useState<EgressLogEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buildingFramework, setBuildingFramework] = useState<EvidencePackFramework | null>(null);
  const [downloadingPackId, setDownloadingPackId] = useState<string | null>(null);
  // DSPM posture roll-up that feeds the board report (from Discovery).
  const [board, setBoard] = useState<DiscoveryBoardSummary | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rulesResp, packsResp, sdfResp, egressResp, boardResp] = await Promise.all([
        listDetectionRules().catch(() => ({ rules: [] })),
        listEvidencePacks({ limit: 50 }).catch(() => ({ packs: [], count: 0 })),
        getSdf().catch(() => ({ sdf: null })),
        listEgressLog({ limit: 200 }).catch(() => ({ entries: [], nextCursor: null })),
        getDiscoveryBoardSummary().catch(() => null),
      ]);
      setRules(rulesResp.rules);
      setPacks(packsResp.packs);
      setSdf(sdfResp.sdf);
      setEgress(egressResp.entries);
      setBoard(boardResp);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load regulatory coverage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleBuildPack(framework: EvidencePackFramework): Promise<void> {
    setBuildingFramework(framework);
    setError(null);
    try {
      // Default reporting window: last 90 days (quarterly cadence
      // matches DPDPA Rules 2025 §22 + SEBI CSCRF audit expectations).
      const periodEnd = new Date();
      const periodStart = new Date(periodEnd.getTime() - 90 * 24 * 60 * 60 * 1000);
      const toYmd = (d: Date) => d.toISOString().slice(0, 10);
      await buildEvidencePack({
        framework,
        periodStart: toYmd(periodStart),
        periodEnd: toYmd(periodEnd),
      });
      // The build is async - poll list once after a short delay.
      setTimeout(() => {
        void refresh();
      }, 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to build evidence pack');
    } finally {
      setBuildingFramework(null);
    }
  }

  // OBS-13 (2026-05-26) - when a pack is already ready, the button
  // downloads the ZIP from the Tier A2 endpoint instead of triggering
  // another build. Auditors expect "Download" not "Build" for an
  // already-attested bundle.
  async function handleDownloadPack(pack: EvidencePack): Promise<void> {
    setDownloadingPackId(pack.id);
    setError(null);
    try {
      await downloadEvidencePack(pack.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to download evidence pack');
    } finally {
      setDownloadingPackId(null);
    }
  }

  // ── derived data ───────────────────────────────────────────────

  const ruleGroups = useMemo(() => {
    if (!rules) return null;
    const groups = new Map<
      ReturnType<typeof categoriseRule>['bucket'],
      { rules: Array<DetectionRule & { citation: string }>; sevCount: Record<string, number> }
    >();
    for (const r of rules) {
      const { bucket, citation } = categoriseRule(r);
      const entry = groups.get(bucket) ?? { rules: [], sevCount: {} };
      entry.rules.push({ ...r, citation });
      entry.sevCount[r.severity] = (entry.sevCount[r.severity] ?? 0) + 1;
      groups.set(bucket, entry);
    }
    return groups;
  }, [rules]);

  const latestPackByFramework = useMemo(() => {
    if (!packs) return null;
    const out = new Map<EvidencePackFramework, EvidencePack>();
    for (const p of packs) {
      const existing = out.get(p.framework);
      if (!existing || new Date(p.generatedAt) > new Date(existing.generatedAt)) {
        out.set(p.framework, p);
      }
    }
    return out;
  }, [packs]);

  const egressRollup = useMemo(() => {
    if (!egress) return null;
    const byDest = new Map<string, { count: number; lastAt: string }>();
    for (const e of egress) {
      const host = (() => {
        try {
          return new URL(e.destinationUrl).host;
        } catch {
          return e.destinationUrl;
        }
      })();
      const existing = byDest.get(host);
      if (!existing) byDest.set(host, { count: 1, lastAt: e.createdAt });
      else {
        existing.count += 1;
        if (e.createdAt > existing.lastAt) existing.lastAt = e.createdAt;
      }
    }
    return Array.from(byDest.entries())
      .map(([host, v]) => ({ host, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [egress]);

  if (loading && !rules) {
    return (
      <PageContainer>
        <GlassCard className="p-10 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> loading regulatory coverage…
        </GlassCard>
      </PageContainer>
    );
  }

  const totalRules = rules?.length ?? 0;
  const totalPacks = packs?.length ?? 0;
  const readyPacks = packs?.filter((p) => p.status === 'ready').length ?? 0;
  const buildingPacks = packs?.filter((p) => p.status === 'building' || p.status === 'pending').length ?? 0;

  return (
    <PageContainer>
      <PageHeader
        title="Compliance · Regulatory Coverage"
        subtitle="What the platform enforces (DDR rules) and what it produces for the regulator (evidence packs), in one view. Quarterly review home for DPO / CISO / Compliance Analyst."
        badges={[
          { label: `${totalRules} rules`, variant: 'glass' },
          { label: `${readyPacks}/${totalPacks} packs ready`, variant: readyPacks > 0 ? 'success' : 'glass' },
          ...(buildingPacks > 0 ? [{ label: `${buildingPacks} building`, variant: 'warning' as const }] : []),
        ]}
        actions={
          <Button variant="ghost" size="sm" onClick={() => void refresh()}>
            <RefreshCw className="h-3 w-3" /> Refresh
          </Button>
        }
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" />
            {error}
          </p>
        </GlassCard>
      )}

      {/* ── KPI strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="DDR rules enforcing" value={String(totalRules)} sub="built-in catalogue" />
        <Kpi label="Frameworks supported" value="4" sub="DPDPA · SEBI · GDPR · CCPA" />
        <Kpi
          label="SDF status (DPDPA §10)"
          value={sdf?.isDesignatedSdf ? 'Designated' : 'Not designated'}
          sub={sdf?.dpoAppointed ? 'DPO appointed' : 'No DPO on file'}
        />
        <Kpi
          label="Egress destinations"
          value={String(egressRollup?.length ?? 0)}
          sub="distinct hosts (last 200 events)"
        />
      </div>

      {/* ── DSPM posture (from Discovery) - feeds the board report ── */}
      <GlassCard className="p-5 mb-6">
        <CardHeader className="p-0 mb-1">
          <CardTitle className="text-sm flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> Data-security posture (from Discovery DSPM)
          </CardTitle>
        </CardHeader>
        <p className="text-[11px] text-muted-foreground mb-3">
          The DSPM picture that feeds the evidence packs below - and the same data FOCTTA Core retrieves for the board report it generates.
        </p>
        {board === null ? (
          <p className="text-xs text-muted-foreground">
            Discovery DSPM data is unavailable right now (no scan yet, or the module is not enabled). The board report will be compliance-only until a scan populates it.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Kpi label="Risk posture" value={`${board.posture.score}/100`} sub={board.posture.band} />
              <Kpi label="Critical open" value={String(board.exposures.criticalOpen)} sub="exposures" />
              <Kpi label="High open" value={String(board.exposures.highOpen)} sub="exposures" />
              <Kpi label="Classified" value={`${Math.round(board.classification.classifiedPct * 100)}%`} sub={`${board.classification.classifiedAssets}/${board.classification.totalAssets} assets`} />
              <Kpi label="Children's data (§9)" value={board.childrensData.status === 'detected' ? String(board.childrensData.minorRecordsDetected) : 'None'} sub={board.childrensData.status === 'detected' ? 'records detected' : 'none detected'} />
            </div>
            {board.exposures.topRisks.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Top data risks</div>
                <div className="space-y-1.5">
                  {board.exposures.topRisks.slice(0, 5).map((r) => (
                    <div key={r.assetRef} className="flex items-center gap-3 text-xs">
                      <Badge variant={r.band === 'critical' ? 'critical' : r.band === 'high' ? 'warning' : 'glass'} className="text-[9px] w-16 justify-center shrink-0">{r.band}</Badge>
                      <span className="font-mono truncate flex-1">{r.name}</span>
                      <span className="text-muted-foreground shrink-0">{r.sourceType}{r.orphan ? ' · no owner' : ''}</span>
                      <span className="tabular text-foreground/80 shrink-0 w-10 text-right">{r.score}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </GlassCard>

      {/* ── Evidence Pack Bundles ── */}
      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
          <Award className="h-4 w-4" /> Evidence pack bundles
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FRAMEWORK_ORDER.map((fw) => {
            const latest = latestPackByFramework?.get(fw) ?? null;
            const meta = FRAMEWORK_META[fw];
            return (
              <GlassCard
                key={fw}
                className={
                  latest?.status === 'ready'
                    ? 'p-4 border-success/40 glow-low'
                    : 'p-4'
                }
              >
                <CardHeader className="p-0 mb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>{meta.displayName}</span>
                    {latest ? (
                      <Badge
                        variant={
                          latest.status === 'ready'
                            ? 'success'
                            : latest.status === 'failed'
                              ? 'critical'
                              : 'warning'
                        }
                        className="text-[10px]"
                      >
                        {latest.status}
                      </Badge>
                    ) : (
                      <Badge variant="glass" className="text-[10px]">never built</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {meta.citation} · {meta.fileCount} files per bundle
                  </CardDescription>
                </CardHeader>
                {latest && (
                  <div className="text-[11px] text-muted-foreground space-y-1 mb-3">
                    <div>
                      Last built:{' '}
                      <span className="text-foreground">
                        {new Date(latest.generatedAt).toLocaleString()}
                      </span>
                    </div>
                    {latest.fileCount !== null && latest.totalBytes !== null && (
                      <div>
                        Bytes: <span className="text-foreground">{latest.totalBytes.toLocaleString()}</span> ·
                        Files: <span className="text-foreground">{latest.fileCount}</span>
                      </div>
                    )}
                    {latest.manifestSignature && (
                      <div className="font-mono truncate">
                        sig:{' '}
                        <span className="text-foreground" title={latest.manifestSignature}>
                          {latest.manifestSignature.slice(0, 16)}…{latest.manifestSignature.slice(-8)}
                        </span>
                      </div>
                    )}
                    {latest.expiresAt && (
                      <div>
                        Expires:{' '}
                        <span className="text-foreground">
                          {new Date(latest.expiresAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {latest.errorMessage && (
                      <div className="text-destructive">err: {latest.errorMessage}</div>
                    )}
                  </div>
                )}
                {/* OBS-13: ready packs get a Download button + a
                    secondary Rebuild link; everything else gets a Build
                    button. The visual switch is the single biggest
                    affordance change an auditor cares about. */}
                {latest?.status === 'ready' ? (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void handleDownloadPack(latest)}
                      disabled={downloadingPackId === latest.id}
                    >
                      {downloadingPackId === latest.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}
                      Download {fw} pack
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void handleBuildPack(fw)}
                      disabled={buildingFramework === fw}
                      title="Rebuild for a fresh 90-day window"
                      className="opacity-70 hover:opacity-100"
                    >
                      {buildingFramework === fw ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : null}
                      Rebuild
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void handleBuildPack(fw)}
                    disabled={buildingFramework === fw}
                  >
                    {buildingFramework === fw ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3" />
                    )}
                    Build {fw} pack (last 90 days)
                  </Button>
                )}
              </GlassCard>
            );
          })}
        </div>
      </section>

      {/* ── DDR Rule Catalogue ── */}
      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> DDR rule catalogue grouped by regulation
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ruleGroups &&
            Array.from(ruleGroups.entries()).map(([bucket, group]) => {
              const meta = BUCKET_META[bucket];
              const Icon = meta.icon;
              return (
                <GlassCard key={bucket} className="p-4">
                  <CardHeader className="p-0 mb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary" />
                      {meta.label}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {group.rules.length} rule{group.rules.length === 1 ? '' : 's'} ·{' '}
                      {Object.entries(group.sevCount)
                        .map(([sev, n]) => `${n} ${sev}`)
                        .join(' / ')}
                    </CardDescription>
                  </CardHeader>
                  <ul className="space-y-1 text-[11px]">
                    {group.rules.slice(0, 10).map((r) => (
                      <li
                        key={r.id}
                        className="flex items-center justify-between gap-2 glass-lite rounded p-1.5"
                      >
                        <span className="font-mono truncate" title={r.name}>
                          {r.name}
                        </span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          <Badge
                            variant={SEVERITY_VARIANT[r.severity] ?? 'glass'}
                            className="text-[9px] uppercase"
                          >
                            {r.severity}
                          </Badge>
                          <span className="text-muted-foreground hidden lg:inline">
                            {r.citation}
                          </span>
                        </span>
                      </li>
                    ))}
                    {group.rules.length > 10 && (
                      <li className="text-[10px] text-muted-foreground text-center pt-1">
                        + {group.rules.length - 10} more
                      </li>
                    )}
                  </ul>
                </GlassCard>
              );
            })}
        </div>
      </section>

      {/* ── SDF Designation ── */}
      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
          <Award className="h-4 w-4" /> SDF designation (DPDPA §10)
        </h2>
        <SdfBlock sdf={sdf} />
      </section>

      {/* ── Cross-Border Inventory ── */}
      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
          <Globe className="h-4 w-4" /> Cross-border egress inventory
        </h2>
        <CrossBorderBlock rollup={egressRollup} />
      </section>
    </PageContainer>
  );
}

// ── Kpi tile (local - KpiCard expects a number value) ─────────────

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }): JSX.Element {
  return (
    <GlassCard className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        {label}
      </div>
      <div className="text-xl font-medium">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </GlassCard>
  );
}

// ── SDF designation block ─────────────────────────────────────────

function SdfBlock({ sdf }: { sdf: SdfObligation | null }): JSX.Element {
  if (!sdf) {
    return (
      <GlassCard className="p-4">
        <p className="text-xs text-muted-foreground">
          No SDF record on file. Visit{' '}
          <a className="text-primary underline" href="/compliance/sdf">
            Compliance → SDF
          </a>{' '}
          to record this tenant's DPDPA §10 status (designated or not, with
          designation basis + DPO appointment evidence).
        </p>
      </GlassCard>
    );
  }
  const checks: Array<{ label: string; ok: boolean; detail?: string }> = [
    {
      label: 'Designation recorded',
      ok: typeof sdf.isDesignatedSdf === 'boolean',
      detail: sdf.isDesignatedSdf ? sdf.designationBasis ?? 'designated' : 'not designated',
    },
    {
      label: 'DPO appointed',
      ok: sdf.dpoAppointed,
      detail: sdf.dpoAppointed ? `${sdf.dpoName ?? ''} ${sdf.dpoEmail ? `<${sdf.dpoEmail}>` : ''}` : 'not appointed',
    },
    {
      label: 'Annual DPIA completed',
      ok: !!sdf.annualDpiaCompletedAt,
      detail: sdf.annualDpiaCompletedAt
        ? new Date(sdf.annualDpiaCompletedAt).toLocaleDateString()
        : 'never',
    },
    {
      label: 'Annual data audit completed',
      ok: !!sdf.annualDataAuditCompletedAt,
      detail: sdf.annualDataAuditCompletedAt
        ? new Date(sdf.annualDataAuditCompletedAt).toLocaleDateString()
        : 'never',
    },
  ];
  return (
    <GlassCard className={sdf.isDesignatedSdf ? 'p-4 border-warning/40' : 'p-4'}>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        {checks.map((c) => (
          <li key={c.label} className="flex items-center gap-2 glass-lite rounded p-2">
            {c.ok ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
            ) : (
              <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
            )}
            <div className="min-w-0">
              <div className="font-medium">{c.label}</div>
              {c.detail && (
                <div className="text-[10px] text-muted-foreground truncate">{c.detail}</div>
              )}
            </div>
          </li>
        ))}
      </ul>
      {sdf.isDesignatedSdf && (
        <div className="mt-3 text-[11px] text-warning flex items-start gap-1.5">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            SDF tenants must complete annual DPIA + data audit + report to the
            Board (Rules 2025 §22). Use the evidence pack builder above to
            produce the DPDPA bundle for board submission.
          </span>
        </div>
      )}
    </GlassCard>
  );
}

// ── Cross-Border egress block ─────────────────────────────────────

function CrossBorderBlock({
  rollup,
}: {
  rollup: Array<{ host: string; count: number; lastAt: string }> | null;
}): JSX.Element {
  if (!rollup || rollup.length === 0) {
    return (
      <GlassCard className="p-4">
        <p className="text-xs text-muted-foreground">
          No egress events in the recent audit chain. Either no metadata has
          left the customer network in this period, or the egress log hasn't
          been populated yet.
        </p>
      </GlassCard>
    );
  }
  // Heuristic: any host that doesn't end in `.in` or contain `foctta`
  // is treated as non-India for the rollup hint. Real adequacy-status data
  // lives in a future `cross_border_register` table.
  const nonIndia = rollup.filter(
    (r) => !r.host.endsWith('.in') && !r.host.includes('foctta') && !r.host.includes('localhost'),
  );
  return (
    <GlassCard className="p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-muted-foreground">
          {rollup.length} distinct destination{rollup.length === 1 ? '' : 's'} in
          recent egress · {nonIndia.length} non-India host{nonIndia.length === 1 ? '' : 's'}
        </p>
        {nonIndia.length > 0 && (
          <Badge variant="warning" className="text-[10px]">
            SCC review required
          </Badge>
        )}
      </div>
      <ul className="space-y-1 text-xs">
        {rollup.slice(0, 15).map((r) => (
          <li
            key={r.host}
            className="flex items-center justify-between gap-2 glass-lite rounded p-2"
          >
            <span className="font-mono truncate" title={r.host}>
              {r.host}
            </span>
            <span className="flex items-center gap-2 text-[10px] text-muted-foreground shrink-0">
              <span>{r.count} event{r.count === 1 ? '' : 's'}</span>
              <span>last {new Date(r.lastAt).toLocaleDateString()}</span>
            </span>
          </li>
        ))}
        {rollup.length > 15 && (
          <li className="text-[10px] text-muted-foreground text-center pt-1">
            + {rollup.length - 15} more - see{' '}
            <a className="text-primary underline" href="/admin/egress-log">
              Admin → Egress Log
            </a>
          </li>
        )}
      </ul>
    </GlassCard>
  );
}
